import { useContext, useEffect, useRef, useState } from 'react';
import Cross from '../../../../../Assets/Modals/closeIcon.svg';
import { useDispatch } from 'react-redux';
import { closeModal, fetchInventoryProducts } from '../../../../../Features/inventoryManagementSlice';
import ChevronDown from '../../../../../Assets/ChevronDownLargeDarkMode.svg';
import { getCategoriesForStore, getCustomCollectionsForStore, updateProduct } from '../../../../../lib/utils/apiCalls';
import { useOutsideClickForTwoRef } from '../../../../../Hooks/useOutsideClickForTwoRef';
import TickMark from '../../../../../Assets/Modals/TickMark.svg';
import CollectionDropdown from './CollectionDropdown';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { toast } from 'react-toastify';
import { InventoryManagementContext } from '../../../../../Context/InventoryManagementContext';

function ProductOrganisation() {
    const dispatch = useDispatch();

    const categoriesDropdownButtonRef = useRef(null);
    const categoriesDropdownRef = useRef(null);

    const customCollectionDropdownButtonRef = useRef(null);
    const customCollectionDropdownRef = useRef(null);

    const { selectedProducts, setSelectedProducts } = useContext(InventoryManagementContext);

    useOutsideClickForTwoRef(categoriesDropdownButtonRef, categoriesDropdownRef, () => {
        setCategoryDropdownOpen(false);
    });

    useOutsideClickForTwoRef(customCollectionDropdownButtonRef, customCollectionDropdownRef, () => {
        setCustomCollectionDropdownOpen(false);
    });

    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [selectedCustomCollections, setSelectedCustomCollections] = useState([]);

    const [categories, setCategories] = useState([]);
    const [customCollections, setCustomCollections] = useState([]);

    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [customCollectionDropdownOpen, setCustomCollectionDropdownOpen] = useState(false);

    const handleCategoryDropdownItemClick = (e, category) => {
        e.stopPropagation();

        let isSelected = selectedCategories.some(selected => selected.id === category.id);

        if(isSelected) {
            let filteredList = selectedCategories.filter(item => item.id !== category.id);
            setSelectedCategories(filteredList);
        }
        else {
            setSelectedCategories([ ...selectedCategories, category]);
        }
    }

    const handleCustomCollectionDropdownItemClick = (e, collection) => {
        e.stopPropagation();

        let isSelected = selectedCustomCollections.some(selected => selected.id === collection.id);

        if(isSelected) {
            let filteredList = selectedCustomCollections.filter(item => item.id !== collection.id);
            setSelectedCustomCollections(filteredList);
        }
        else {
            setSelectedCustomCollections([ ...selectedCustomCollections, collection ]);
        }
    }

    const toggleCategoryDropdown = () => {
        setCategoryDropdownOpen(!categoryDropdownOpen);
    }

    const toggleCustomCollectionDropdown = () => {
        setCustomCollectionDropdownOpen(!customCollectionDropdownOpen);
    }

    const [buttonAvailable, setButtonAvailable] = useState(false);

    const handleNext = async () => {
        if(buttonAvailable) {
            if(selectedProducts.length > 0) {
                const callData = {
                    collectionIds: selectedCollections.map(item => item.id),
                    customCollectionId: selectedCustomCollections.map(item => item.id),
                }

                const updatePromises = selectedProducts.map(productId => updateProduct(callData, productId));
                const updateAllPromise = Promise.all(updatePromises);

                toast.promise(
                    updateAllPromise,
                    {
                        pending: {
                            render() {
                                return 'Updating product organisation...';
                            },
                            className: 'toast-pending',
                        },
                        success: {
                            render() {
                                return 'Product organisation updated successfully.';
                            },
                        },
                    }
                );

                try {
                    await updateAllPromise;
                    dispatch(fetchInventoryProducts());
                    dispatch(closeModal());
                    setSelectedProducts([]);
                }
                catch (err) {
                    showToast('Failed to update product organisation. Please try again.', 'error');
                }
            }
        }
        else {
            if(selectedCollections.length === 0 && selectedCustomCollections.length === 0) {
                showToast('Please select a collection.', 'warning');
            }
            return;
        }
    }

    const handleCloseModal = () => {
        dispatch(closeModal());
    }

    useEffect(() => {
        const fetchStoreCategories = async () => {
            try {
                const response = await getCategoriesForStore();
                setCategories(response.category);
            }
            catch(err) {
                console.log(err);
            }
        }

        const fetchStoreCustomCollections = async () => {
            try {
                const response = await getCustomCollectionsForStore();
                setCustomCollections(response.collection);
            }
            catch(err) {
                console.log(err);
            }
        }

        fetchStoreCategories();
        fetchStoreCustomCollections();
    }, []);

    useEffect(() => {
        if(selectedCollections.length > 0 || selectedCustomCollections.length > 0) {
            setButtonAvailable(true);
        }
        else {
            setButtonAvailable(false);
        }
    }, [selectedCollections, selectedCustomCollections]);

    return (
        <div className='w-[44%] bg-[#FFFFFF] border border-[#EAEAEA] rounded-[9px] p-4 flex flex-col gap-4'>
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h1 className='font-semibold text-2xl text-[#121212]'>Product Organisation</h1>
                    </div>

                    <button onClick={handleCloseModal} className="w-11 h-11 flex items-center justify-center">
                        <img src={Cross} alt="" />
                    </button>
                </div>

                <div className="flex flex-col gap-2">
                    <h1 className='select-none cursor-pointer font-medium text-base text-[#121212]'>Select category</h1>

                    <div onClick={toggleCategoryDropdown} ref={categoriesDropdownButtonRef} className="relative cursor-pointer px-3 py-4 border rounded-[9px] border-[#EAEAEA] bg-[#F5F5F5] flex items-center gap-[10px]">
                        <div className="w-[calc(100%-24px)]">
                            <p className='font-normal select-none text-base text-[#666666] whitespace-nowrap overflow-hidden text-ellipsis'>
                                {selectedCategories.length > 0 ? selectedCategories.map(item => item.categoryName).join(', ') : 'select category'}
                            </p>
                        </div>

                        <div className="w-6 h-6 flex items-center justify-center select-none">
                            <img src={ChevronDown} alt="" />
                        </div>

                        {
                            categoryDropdownOpen && 
                            <div ref={categoriesDropdownRef} className="absolute z-10 top-14 left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]">
                                {categories.map(category => {
                                    let isSelected = selectedCategories.some(selected => selected.id === category.id);

                                    return (
                                        <div key={category.id} onClick={(e) => handleCategoryDropdownItemClick(e, category)} className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5]">
                                            <div className={`w-6 h-6 rounded-md ${isSelected ? 'border-none bg-primaryAccent flex items-center justify-center' : 'border border-[#D1D5DB]'} `} >
                                                <img className='select-none' src={isSelected ? TickMark : undefined} alt="" />
                                            </div>

                                            <p className='font-medium select-none text-base text-[#666666]'>
                                                {category.categoryName}
                                            </p>
                                        </div>
                                    )
                                })}
                            </div>
                        }
                    </div>
                </div>

                {
                    selectedCategories.length > 0 && 
                    <>
                        {
                            selectedCategories.map(category => {
                                return (
                                    <CollectionDropdown 
                                        key={category.id} 
                                        category={category} 
                                        selectedCollections={selectedCollections} 
                                        updateSelectedCollections={value => setSelectedCollections(value)} 
                                    />
                                )
                            })
                        }
                    </>
                }

                <div className="flex flex-col gap-2">
                    <h1 className='select-none cursor-pointer font-medium text-base text-[#121212]'>Add to custom collection</h1>

                    <div onClick={toggleCustomCollectionDropdown} ref={customCollectionDropdownButtonRef} className="relative cursor-pointer px-3 py-4 border rounded-[9px] border-[#EAEAEA] bg-[#F5F5F5] flex items-center gap-[10px]">
                        <div className="w-[calc(100%-24px)]">
                            <p className='font-normal select-none text-base text-[#666666] whitespace-nowrap overflow-hidden text-ellipsis'>
                                {selectedCustomCollections.length > 0 ? selectedCustomCollections.map(item => item.CollectionName).join(', ') : 'select custom collection'}
                            </p>
                        </div>

                        <div className="select-none w-6 h-6 flex items-center justify-center">
                            <img src={ChevronDown} alt="" />
                        </div>

                        {
                            customCollectionDropdownOpen && 
                            <div ref={customCollectionDropdownRef} className="absolute z-10 top-14 left-0 w-full border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0px_0px_12px_0px_#0000003D]">
                                {customCollections.map(collection => {
                                    let isSelected = selectedCustomCollections.some(selected => selected.id === collection.id);

                                    return (
                                        <div key={collection.id} onClick={(e) => handleCustomCollectionDropdownItemClick(e, collection)} className="w-full px-3 py-4 flex gap-[10px] bg-[#FFFFFF] hover:bg-[#F5F5F5]">
                                            <div className={`w-6 h-6 rounded-md ${isSelected ? 'border-none bg-primaryAccent flex items-center justify-center' : 'border border-[#D1D5DB]'} `} >
                                                <img className='select-none' src={isSelected ? TickMark : undefined} alt="" />
                                            </div>

                                            <p className='font-medium select-none text-base text-[#666666]'>
                                                {collection.CollectionName}
                                            </p>
                                        </div>
                                    )
                                })}
                            </div>
                        }
                    </div>
                </div>
            </div>

            <button onClick={handleNext} className={`px-4 py-2 ${buttonAvailable ? 'bg-primaryColor hover:bg-primaryAccent' : 'bg-buttonDisabledBg cursor-not-allowed'} text-buttonText rounded font-semibold text-base`}>Add to organisation</button>
        </div>
    )
}

export default ProductOrganisation;
